import { useState, useCallback, useEffect } from 'react';

/**
 * Copy content to the clipboard.
 *
 * @param {number|null} interval Reset the 'isCopied' value after the given interval.
 *
 * @returns {Array} The copied state and the copy handler.
 */
export default function useCopyToClipboard(interval = null) {
  const [isCopied, setCopied] = useState(false);

  // Copy the passed content to the clipboard.
  const handleCopy = useCallback((content) => {
    // Define an element, set the content, select it and copy the content.
    const element = document.createElement('textarea');
    element.setAttribute('readonly', '');
    element.style.display = 'hidden';
    element.value = content;

    // Append to the body so it is visible.
    document.body.appendChild(element);

    // Select the text of the fake element.
    element.select();

    // Copy the current selection.
    document.execCommand('copy');

    // Remove the element from the DOM.
    document.body.removeChild(element);

    setCopied(true);
  }, []);

  // Reset after the given interval.
  useEffect(() => {
    let timeout;
    if (isCopied && interval) {
      timeout = setTimeout(() => setCopied(false), interval);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [isCopied, interval]);

  return [isCopied, handleCopy];
}
